/* ==========================================================================
   cirrus-terms-gate.js — require affirmative acceptance of the Terms of Sale
   & Product Use before checkout, and record that acceptance on the order.

   Two jobs:

   1. GATE. Until the box is ticked, the checkout button is disabled and the
      accelerated wallet buttons (Shop Pay / PayPal / Google Pay) are made
      non-interactive. The wallet buttons matter: they are a separate path
      straight to checkout that ignores the cart form entirely, so disabling
      only #checkout would leave the gate wide open.

   2. RECORD. Ticking the box POSTs cart attributes, which Shopify carries onto
      the order and shows under "Additional details":
        terms_accepted, terms_version, terms_effective_date, terms_accepted_at

   The cart drawer re-renders (morphs) on every quantity change, so everything
   here is delegated or re-applied via MutationObserver rather than bound once.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__c3dTermsGate) return;
  window.__c3dTermsGate = true;

  var SEL_GATE = '[data-c3d-gate]';
  var SEL_BOX = '[data-c3d-gate-box]';
  var SEL_HINT = '[data-c3d-gate-hint]';
  var SEL_CTAS = '.cart__ctas';
  var SEL_CHECKOUT = '#checkout, [name="checkout"]';
  var SEL_WALLETS = '.additional-checkout-buttons';

  function gates() {
    return Array.prototype.slice.call(document.querySelectorAll(SEL_GATE));
  }

  /* A gate governs the .cart__ctas block it was rendered next to. */
  function ctasFor(gate) {
    var scope = gate.closest('.cart-summary, .cart-drawer, cart-drawer-component, form, body') || document;
    return scope.querySelector(SEL_CTAS) || document.querySelector(SEL_CTAS);
  }

  function apply(gate) {
    var box = gate.querySelector(SEL_BOX);
    if (!box) return;
    var on = !!box.checked;
    var ctas = ctasFor(gate);
    if (!ctas) return;

    gate.classList.toggle('is-accepted', on);

    var btn = ctas.querySelector(SEL_CHECKOUT);
    if (btn) {
      /* never re-enable a button Shopify disabled for its own reasons (empty cart) */
      if (on) {
        if (btn.getAttribute('data-c3d-locked') === '1') {
          btn.disabled = false;
          btn.removeAttribute('data-c3d-locked');
        }
      } else if (!btn.disabled) {
        btn.disabled = true;
        btn.setAttribute('data-c3d-locked', '1');
      }
      btn.setAttribute('aria-describedby', gate.querySelector(SEL_HINT) ? gate.querySelector(SEL_HINT).id || '' : '');
    }

    var wallets = ctas.querySelector(SEL_WALLETS);
    if (wallets) wallets.classList.toggle('c3d-wallets-locked', !on);

    var hint = gate.querySelector(SEL_HINT);
    if (hint) hint.hidden = on;
  }

  function applyAll() { gates().forEach(apply); }

  function nudge(gate) {
    var hint = gate.querySelector(SEL_HINT);
    if (hint) hint.hidden = false;
    gate.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    gate.classList.remove('c3d-gate--nudge');
    /* restart the animation */
    void gate.offsetWidth;
    gate.classList.add('c3d-gate--nudge');
    var box = gate.querySelector(SEL_BOX);
    if (box) box.focus({ preventScroll: false });
  }

  /* ---- record acceptance on the cart, which carries it to the order ---- */
  function record(gate, on) {
    var attrs = {};
    if (on) {
      attrs['terms_accepted'] = 'true';
      attrs['terms_version'] = gate.getAttribute('data-terms-version') || '';
      attrs['terms_effective_date'] = gate.getAttribute('data-terms-effective') || '';
      attrs['terms_accepted_at'] = new Date().toISOString();
    } else {
      /* empty string removes the attribute */
      attrs['terms_accepted'] = '';
      attrs['terms_version'] = '';
      attrs['terms_effective_date'] = '';
      attrs['terms_accepted_at'] = '';
    }
    return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/update.js'
        : '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () { /* the gate still holds locally if this fails */ });
  }

  /* ---- delegated events (survive drawer re-renders) ---- */
  document.addEventListener('change', function (e) {
    var box = e.target.closest && e.target.closest(SEL_BOX);
    if (!box) return;
    var gate = box.closest(SEL_GATE);
    if (!gate) return;
    apply(gate);
    record(gate, !!box.checked);
    /* keep every rendered copy of the gate in sync (drawer + page) */
    gates().forEach(function (g) {
      var b = g.querySelector(SEL_BOX);
      if (b && b !== box) { b.checked = box.checked; apply(g); }
    });
  }, true);

  /* Catch clicks on a locked checkout button or locked wallets and explain why. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var hitLockedWallets = t.closest('.c3d-wallets-locked');
    var btn = t.closest(SEL_CHECKOUT);
    var hitLockedBtn = btn && btn.getAttribute('data-c3d-locked') === '1';
    if (!hitLockedWallets && !hitLockedBtn) return;
    var ctas = (hitLockedWallets || btn).closest(SEL_CTAS);
    var gate = null;
    if (ctas) {
      var scope = ctas.closest('.cart-summary, .cart-drawer, cart-drawer-component, form, body') || document;
      gate = scope.querySelector(SEL_GATE) || document.querySelector(SEL_GATE);
    }
    e.preventDefault();
    e.stopPropagation();
    if (gate) nudge(gate);
  }, true);

  /* Belt and braces: block form submission if somehow it gets that far. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.querySelector) return;
    if (!form.querySelector(SEL_CHECKOUT)) return;
    var gate = document.querySelector(SEL_GATE);
    if (!gate) return;
    var box = gate.querySelector(SEL_BOX);
    if (box && !box.checked) {
      e.preventDefault();
      e.stopPropagation();
      nudge(gate);
    }
  }, true);

  /* The drawer replaces its innards wholesale; re-apply after every mutation. */
  var pending = null;
  var mo = new MutationObserver(function () {
    if (pending) return;
    pending = requestAnimationFrame(function () { pending = null; applyAll(); });
  });

  function boot() {
    applyAll();
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', applyAll);
  document.addEventListener('shopify:cart:lines-update', applyAll);
})();
